import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { jwtDecode } from 'jwt-decode';
import { fetchEntitlement } from '../../../store/actions/entitlementActions';
import { fetchPaymentHistory } from '../../../store/actions/userActions';
import { BASE_URL } from '../../../constants';
import { SUBSCRIPTION_TIERS, isTierAvailableForRole } from '../../../config/stripeConfig';
import { Badge, Btn, Card, ConfirmModal, EmptyState, Overline, PageHeader, SegmentedControl, Tile } from '../../ForBrands/ui/BrandUI';
import Icon from '../../ForBrands/ui/icons';
import { planState } from '../../ForCoaches/v2/profile/subscriptionStatus';
import { usePhoneTheme } from '../../ForBrands/useIsPhone';
import { CacheSetUpNote, cachePlanEnd, planBackingOf } from '../../ForAthletes/Billing/AthleteBillingPage';
import '../../ForBrands/brandMode.css';

// Billing & subscriptions in the V2 style shared by Coach Mode (Inter body,
// Archivo headings, bm type scale, pill buttons). It sits inside a V2 shell,
// which supplies the frame: Coach Mode (/coach/billing), Brand Mode for pure
// brands (/brand/billing) and the Player Index for recruiters, AD/PD and
// brands who are staff (/business/billing). On any other path the page wraps
// itself in the same root so the treatment matches.

// The shell the page sits in, from the path: its billing address (where the
// Stripe return lands and the query is cleared to) and its eyebrow.
const SHELLS = [
  { prefix: '/coach', billingPath: '/coach/billing', eyebrow: 'Coach Mode' },
  { prefix: '/brand', billingPath: '/brand/billing', eyebrow: 'Brand Mode' },
  { prefix: '/business', billingPath: '/business/billing', eyebrow: 'Account' },
];
export const billingShellFor = (pathname = '') => {
  const shell = SHELLS.find((s) => pathname === s.prefix || pathname.startsWith(`${s.prefix}/`));
  return shell ? { ...shell, inShell: true } : { ...SHELLS[2], inShell: false };
};

const INTERVALS = [
  { value: 'annual', label: 'Annual' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'monthly', label: 'Monthly' },
];

const INTERVAL_NOUN = { annual: 'year', quarterly: 'quarter', monthly: 'month' };
const MONTHS_IN = { annual: 12, quarterly: 3, monthly: 1 };

// The entitlements the server gives an account with no paid plan.
const FREE_ENTITLEMENTS = ['coach_free', 'public'];

const formatPrice = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

const formatDate = (value, unix = false) =>
  new Date(unix ? value * 1000 : value).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const savingsPercent = (tier, interval) => {
  if (interval === 'monthly' || !tier.pricing[interval]) return null;
  const monthlyTotal = tier.pricing.monthly * MONTHS_IN[interval];
  return Math.round(((monthlyTotal - tier.pricing[interval]) / monthlyTotal) * 100);
};

const CheckIcon = () => (
  <span className="mt-[2px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-bm-orange text-white">
    <Icon name="check" size={11} strokeWidth={2.5} />
  </span>
);

const FeatureList = ({ features }) => (
  <ul className="flex flex-col gap-[10px]">
    {features.map((feature) => (
      <li key={feature} className="flex items-start gap-[10px] bm-body-m text-bm-ink">
        <CheckIcon />
        <span>{feature}</span>
      </li>
    ))}
  </ul>
);

const PlanCard = ({ tier, interval, current, currentInterval, popular, busy, onChoose, onContact }) => {
  const custom = !!tier.pricing.custom;
  const price = custom ? null : tier.pricing[interval];
  const perMonth = custom || interval === 'monthly' ? null : price / MONTHS_IN[interval];
  const savings = custom ? null : savingsPercent(tier, interval);
  // No stored interval (invoice plans, older records) still counts as the current plan.
  const sameInterval = current && (!currentInterval || currentInterval.toLowerCase() === interval);
  let cta = `Choose ${tier.name}`;
  if (current && sameInterval) cta = 'Current plan';
  else if (current) cta = `Switch to ${interval}`;

  return (
    <Card padding="p-6" className={`relative flex min-w-0 flex-1 flex-col gap-5 ${popular ? 'border-bm-orange' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <Overline>{tier.name}</Overline>
          <p className="bm-body-m text-bm-muted">{tier.description}</p>
        </div>
        {current ? <Badge tone="dark">Current plan</Badge> : popular ? <Badge tone="brand">Most popular</Badge> : null}
      </div>

      {custom ? (
        <div className="flex flex-col gap-1">
          <div className="bm-num-l text-bm-ink">Custom</div>
          <div className="bm-body-s text-bm-muted">Priced per sport for your program</div>
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <div className="flex items-baseline gap-2">
            <span className="bm-num-l text-bm-ink">{formatPrice(price)}</span>
            <span className="bm-body-m text-bm-muted">per {INTERVAL_NOUN[interval]}</span>
          </div>
          <div className="flex items-center gap-2">
            {perMonth != null && <span className="bm-body-s text-bm-muted">{formatPrice(perMonth)} per month</span>}
            {savings ? <Badge tone="soft">Save {savings}%</Badge> : null}
          </div>
        </div>
      )}

      {custom ? (
        <Btn variant="secondary" onClick={onContact}>
          Contact sales
        </Btn>
      ) : (
        <Btn variant={popular && !current ? 'primary' : 'secondary'} onClick={() => onChoose(tier, interval)} disabled={busy || (current && sameInterval)}>
          {busy ? 'Opening checkout...' : cta}
        </Btn>
      )}

      <div className="border-t border-bm-border" />
      <FeatureList features={tier.features} />
      {!custom && <p className="bm-body-s text-bm-tertiary">Card or bank transfer through Stripe. 5-day free trial on new plans.</p>}
      {custom && <p className="bm-body-s text-bm-tertiary">Dedicated support, AI insights by sport and invoice billing.</p>}
    </Card>
  );
};

const CoachBillingPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [interval, setInterval] = useState('annual');
  const [refreshKey, setRefreshKey] = useState(0);

  const entitlement = useSelector((state) => state.entitlement);
  // payments/history: whether a Stripe subscription stands behind the plan.
  const account = useSelector((state) => state.user.paymentHistory);
  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.user.user);
  const decoded = useMemo(() => (token ? jwtDecode(token) : { permissions: [] }), [token]);
  const { billingPath, eyebrow, inShell } = billingShellFor(location.pathname);
  // Coach Mode on a phone: the billing note stacks its badge over the text.
  const phone = usePhoneTheme();

  const availableTiers = Object.keys(SUBSCRIPTION_TIERS).filter(
    (tierId) => isTierAvailableForRole(tierId, decoded.permissions) && tierId !== 'COACH_FREE' && tierId !== 'ATHLETE_PREMIUM',
  );

  // Payment success / cancel bounce-back from Stripe checkout.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const payment = params.get('payment');
    if (payment === 'success') {
      toast.success('Payment successful. Your subscription is now active.');
      setRefreshKey((prev) => prev + 1);
      navigate(billingPath, { replace: true });
    } else if (payment === 'cancelled') {
      toast.info('Payment was cancelled. You can try again when ready.');
      navigate(billingPath, { replace: true });
    }
  }, [location.search, navigate, billingPath]);

  useEffect(() => {
    dispatch(fetchEntitlement());
    dispatch(fetchPaymentHistory());
  }, [dispatch, refreshKey]);

  // Default the interval picker to what the coach already pays on.
  useEffect(() => {
    const current = (entitlement.billingInterval || '').toLowerCase();
    if (INTERVALS.some((i) => i.value === current)) setInterval(current);
  }, [entitlement.billingInterval]);

  const authed = { headers: { Authorization: `Bearer ${token}` } };

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/api/users/stripe/customer-portal/`, {}, authed);
      if (response.data.url) window.location.href = response.data.url;
    } catch (error) {
      console.error('Error accessing customer portal:', error);
      toast.error('Could not open the billing portal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Asked in the V2 confirm modal first (Cancel subscription opens it).
  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      await axios.post(`${BASE_URL}/api/users/stripe/cancel-subscription/`, {}, authed);
      toast.success('Subscription cancelled. You keep access until the end of your billing period.');
      setConfirmCancel(false);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error(error.response?.data?.error || 'Could not cancel the subscription. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleResumeSubscription = async () => {
    setLoading(true);
    try {
      await axios.post(`${BASE_URL}/api/users/stripe/resume-subscription/`, {}, authed);
      toast.success('Subscription resumed.');
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error('Error resuming subscription:', error);
      toast.error(error.response?.data?.error || 'Could not resume the subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleContactSales = () => {
    const subject = encodeURIComponent('Premium plan inquiry');
    const body = encodeURIComponent(
      `Hi CACHE sales team,\n\nI'm interested in the Premium plan for my program.\n\nName: ${user?.firstName || ''} ${user?.lastName || ''}\nEmail: ${user?.username || user?.email || decoded.email || ''}\nOrganization: \n\nPlease reach out to discuss pricing per sport, implementation and support.\n\nThanks`,
    );
    window.location.href = `mailto:sales@cachescore.com?subject=${subject}&body=${body}`;
  };

  const handleSubscribe = async (tier, chosenInterval) => {
    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/api/users/stripe/create-checkout-session/`, { tier: tier.id, interval: chosenInterval }, authed);
      if (response.data.upgraded) {
        toast.success('Subscription updated. Proration applies to your next invoice.');
        setRefreshKey((prev) => prev + 1);
        setCheckoutLoading(false);
      } else if (response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        setCheckoutLoading(false);
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error(error.response?.data?.error || 'Could not start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  const tierKey = (entitlement.subscriptionTier || '').toUpperCase();
  const plan = planState(entitlement);
  const { status, isPendingCancellation, isEnded } = plan;
  // A plan CACHE set up (Manage tiers, a paid invoice) has no Stripe
  // subscription, so the Stripe portal, cancel and resume would only fail:
  // it says who to contact instead. Neither shows until payments/history
  // says which it is. Once the server puts the account on a free plan (a
  // grant past its end date) that plan has ended. Only a paid tier had such
  // a plan (plan.hasPlan): an older free record that still says 'active'
  // keeps its status label as before.
  const backing = planBackingOf(entitlement, account);
  const cachePlanEnded = plan.hasPlan && plan.isActiveSubscription && backing === 'cache' && FREE_ENTITLEMENTS.includes(entitlement.entitlement);
  const isActiveSubscription = plan.isActiveSubscription && !cachePlanEnded;
  const hasPlan = plan.hasPlan && !cachePlanEnded;
  const statusMeta = cachePlanEnded ? { label: status === 'trialing' ? 'Trial ended' : 'Plan ended', tone: 'passive' } : plan.statusMeta;
  const canManage = hasPlan && backing === 'stripe';
  const cachePlan = hasPlan && backing === 'cache';

  const tiles = [];
  tiles.push({ label: 'Plan', value: hasPlan ? entitlement.displayName || SUBSCRIPTION_TIERS[tierKey]?.name || 'Paid plan' : 'Free' });
  tiles.push({ label: 'Status', value: statusMeta ? statusMeta.label : 'Free', accent: isPendingCancellation });
  if (hasPlan && entitlement.billingInterval && isActiveSubscription) {
    tiles.push({ label: 'Billing interval', value: entitlement.billingInterval.charAt(0).toUpperCase() + entitlement.billingInterval.slice(1) });
  }
  // Nothing is billed on a plan CACHE set up: it runs to its end date (sent
  // as an ISO string). Stripe sends its dates as unix seconds.
  const cacheEnds = cachePlan ? cachePlanEnd(entitlement, status) : null;
  if (cacheEnds) {
    tiles.push({ label: status === 'trialing' ? 'Trial ends' : 'Subscription expires', value: formatDate(cacheEnds), accent: status === 'trialing' });
  } else if (canManage && status === 'trialing' && entitlement.nextPaymentDate) {
    tiles.push({ label: 'Trial ends', value: formatDate(entitlement.nextPaymentDate, true), accent: true });
  } else if (canManage && entitlement.nextPaymentDate) {
    tiles.push({ label: isPendingCancellation ? 'Access ends' : 'Next billing date', value: formatDate(entitlement.nextPaymentDate, true), accent: isPendingCancellation });
  }

  const planName = entitlement.displayName || 'a paid plan';
  let subtitle = 'Choose a plan to unlock CacheValue™, CacheShare™ and NIL intelligence for your roster.';
  if (cachePlan) subtitle = `You are on ${planName}, set up for you by CACHE.`;
  else if (canManage) subtitle = `You are on ${planName}. Manage your plan, billing interval and payment details here.`;
  else if (hasPlan) subtitle = `You are on ${planName}.`;

  const content = (
    <div>
      <PageHeader
        eyebrow={eyebrow}
        title="Billing & subscriptions"
        subtitle={subtitle}
        actions={
          canManage ? (
            <Btn onClick={handleManageSubscription} disabled={loading}>
              {loading ? 'Opening...' : 'Manage subscription'}
            </Btn>
          ) : null
        }
      />

      <div className="flex flex-col gap-4 pt-2">
        <Card padding="p-6" className="flex flex-col gap-5">
          <div className="flex items-center justify-between gap-3">
            <Overline>Current plan</Overline>
            {statusMeta && (
              <Badge tone={statusMeta.tone} dot={statusMeta.dot}>
                {statusMeta.label}
              </Badge>
            )}
          </div>

          {entitlement.loading && !entitlement.subscriptionTier ? (
            <p className="bm-body-m text-bm-muted">Loading your subscription...</p>
          ) : hasPlan || isEnded ? (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {tiles.map((t) => (
                <Tile key={t.label} label={t.label} value={t.value} accent={t.accent} />
              ))}
            </div>
          ) : (
            <EmptyState action={<Btn onClick={() => document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' })}>See plans</Btn>}>
              No active subscription. Pick a plan below and your roster unlocks right after checkout.
            </EmptyState>
          )}

          {isPendingCancellation && canManage && entitlement.nextPaymentDate && (
            <div className="flex flex-col gap-3 rounded-[8px] border border-bm-border bg-bm-subtle px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="bm-body-m text-bm-ink">
                Your subscription is set to cancel. You keep every premium feature until {formatDate(entitlement.nextPaymentDate, true)}.
              </p>
              <Btn onClick={handleResumeSubscription} disabled={loading}>
                {loading ? 'Resuming...' : 'Resume subscription'}
              </Btn>
            </div>
          )}
          {isEnded && (
            <div className="rounded-[8px] border border-bm-border bg-bm-subtle px-5 py-4">
              <p className="bm-body-m text-bm-ink">Your subscription has ended. Choose a plan below to regain access to premium features.</p>
            </div>
          )}

          {cachePlan && <CacheSetUpNote />}

          {canManage && isActiveSubscription && (
            <div className="flex flex-wrap items-center gap-3 border-t border-bm-border pt-5">
              <Btn variant="secondary" onClick={handleManageSubscription} disabled={loading}>
                {loading ? 'Opening...' : 'Update payment method'}
              </Btn>
              <Btn variant="danger" onClick={() => setConfirmCancel(true)} disabled={cancelLoading}>
                {cancelLoading ? 'Cancelling...' : 'Cancel subscription'}
              </Btn>
              <span className="bm-body-s text-bm-tertiary">Invoices, receipts and payment methods live in the Stripe portal.</span>
            </div>
          )}
        </Card>

        <div id="plans" className="flex flex-col gap-4 pt-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-col gap-1">
              <Overline>{hasPlan ? 'Change plan' : 'Choose your plan'}</Overline>
              <h2 className="bm-h-m text-bm-ink">Plans for coaches and programs</h2>
              <p className="bm-body-m text-bm-muted">Pick a tier and a billing interval. Longer intervals cost less per month.</p>
            </div>
            <SegmentedControl value={interval} onChange={setInterval} options={INTERVALS} />
          </div>

          <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch">
            {availableTiers.includes('COACH_CORE') && (
              <PlanCard tier={SUBSCRIPTION_TIERS.COACH_CORE} interval={interval} current={tierKey === 'COACH_CORE' && hasPlan} currentInterval={entitlement.billingInterval} busy={checkoutLoading} onChoose={handleSubscribe} />
            )}
            {availableTiers.includes('COACH_PRO') && (
              <PlanCard tier={SUBSCRIPTION_TIERS.COACH_PRO} interval={interval} current={tierKey === 'COACH_PRO' && hasPlan} currentInterval={entitlement.billingInterval} popular busy={checkoutLoading} onChoose={handleSubscribe} />
            )}
            {availableTiers.includes('COACH_PREMIUM') && (
              <PlanCard tier={SUBSCRIPTION_TIERS.COACH_PREMIUM} interval={interval} current={tierKey === 'COACH_PREMIUM' && hasPlan} currentInterval={entitlement.billingInterval} onContact={handleContactSales} />
            )}
          </div>

          <Card padding="px-5 py-4" className={phone ? 'flex flex-col items-start gap-3' : 'flex items-start gap-3'}>
            <Badge tone="outline" size="m">
              How billing works
            </Badge>
            <p className="bm-body-m text-bm-muted">
              Core and Pro check out through Stripe with a card or bank transfer and activate right away, with a 5-day free trial on a new plan. Switching
              tiers on an active plan is prorated on your next invoice. Premium is priced per sport and billed by invoice through our sales team.
            </p>
          </Card>
        </div>
      </div>

      {confirmCancel && (
        <ConfirmModal
          title="Cancel your subscription?"
          body="Your plan stops renewing. You keep every premium feature until the end of the current billing period."
          ackLabel="I understand my plan ends at the end of this billing period."
          confirmLabel="Cancel subscription"
          tone="danger"
          busy={cancelLoading}
          onConfirm={handleCancelSubscription}
          onClose={() => setConfirmCancel(false)}
        />
      )}
    </div>
  );

  if (inShell) return content;
  return <div className="bm-root min-h-screen bg-white px-5 pb-[70px] sm:px-8 lg:px-12 min-[1440px]:px-[122px]">{content}</div>;
};

export default CoachBillingPage;
